import { useMemo } from 'react'

import { useMyMonitoringGetSiteEventsInfinite } from '@apps-common/apis/my-monitoring/site'
import { InfiniteLoader, NoResultsCard, PullRefresh } from '@apps-common/components/common'
import {
  ModalBodyInfiniteScrollable,
  ModalCloseButton,
  ModalContainer,
  ModalHeader,
  ModalHeaderContainer
} from '@apps-common/components/modals/modal'
import { ModalDismiss } from '@apps-common/config/modal'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { IMMSiteModel } from '@apps-common/types/my-monitoring'
import { IonInfiniteScroll } from '@ionic/react'
import { AnimatePresence, motion } from 'framer-motion'
import { DEFAULT_SITE_EVENTS_TAKE } from './site-events-constants'
import { SiteEventItem, SiteEventItemSkeleton } from './SiteEventItem'

interface IProps {
  // open: boolean
  // close: () => void
  site?: IMMSiteModel
}

export const SiteEventsModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: IProps
}) => {
  const { site } = componentProps

  const { isLoading, isError, data, hasNextPage, fetchNextPage, refetch, isFetching } = useMyMonitoringGetSiteEventsInfinite({
    config: { enabled: !!site },
    identifiers: `${site?.ipAddress},${site?.imei}`,
    siteId: site?.unitId || '',
    take: DEFAULT_SITE_EVENTS_TAKE
  })

  const allData = useMemo(() => data?.pages?.reduce((acc, curr) => [...acc, ...curr], []) || [], [data])

  const loading = isLoading || isError

  return (
    <ModalContainer
      containerClasses=''
      innerClasses='!shadow-md md:!max-w-[420px] !min-h-[calc(100dvh-30px)] !h-[calc(100dvh-30px)] !mx-[1rem] md:!ml-auto md:!mr-[15px]'
      onDismiss={onDismiss}
    >
      <ModalHeaderContainer>
        <div className='flex items-start justify-between'>
          <div className='w-[85%]'>
            <ModalHeader subtitle={site?.siteName} title='Events' subtitleClasses='capitalize' />
          </div>
          <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
        </div>
      </ModalHeaderContainer>
      <ModalBodyInfiniteScrollable classes='!pt-[80px] flex flex-col gap-3'>
        <PullRefresh loading={loading} callback={refetch} />

        <AnimatePresence mode='wait'>
          {loading && (
            <>
              {Array.from(Array(DEFAULT_SITE_EVENTS_TAKE).keys()).map((k, i) => (
                <motion.div key={`site-event-item-skeleton-${k}-${i}`} {...motionFadeIn(0.2)}>
                  <SiteEventItemSkeleton />
                </motion.div>
              ))}
              <div className='mx-auto bg-skeleton rounded-full w-[105px] h-[26px] animate-pulse' />
            </>
          )}
          {!loading &&
            allData?.map((d, i) => (
              <motion.div key={`site-event-item-${d.id}-${i}`} {...motionFadeIn(0.2)}>
                <SiteEventItem containerClasses='!bg-surface' data={d} />
              </motion.div>
            ))}
          {!loading && allData.length === 0 && (
            <motion.div key={`site-no-events`} {...motionFadeIn(0.2)} className='flex items-center justify-center pt-2'>
              <NoResultsCard classes='bg-surface w-full'>This site currently has no events to display.</NoResultsCard>
            </motion.div>
          )}
        </AnimatePresence>
        <IonInfiniteScroll
          onIonInfinite={async (ev) => {
            await fetchNextPage()
            ev.target.complete()
          }}
        >
          <InfiniteLoader key='notifications-modal-list-infinite-loader' loading={isFetching} />
        </IonInfiniteScroll>
      </ModalBodyInfiniteScrollable>
    </ModalContainer>
  )
}
